import {
  IconChevronDown,
  IconDots,
  IconLayoutDashboard,
  IconUsers,
  IconBuildingSkyscraper,
  IconShoppingCart,
  IconMessageReport,
  IconChartBar,
  IconReceipt,
  IconFileAnalytics,
  IconWallet,
  IconCoins,
  IconBox,
} from "@tabler/icons-react";
import { Group, Menu, Text, Box, ActionIcon, Tooltip } from "@mantine/core";
import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";

const iconMap: Record<string, any> = {
  DASHBOARD: IconLayoutDashboard,
  PEOPLE: IconUsers,
  FACILITIES: IconBuildingSkyscraper,
  STORE: IconShoppingCart,
  ENQUIRY: IconMessageReport,
  SALES: IconChartBar,
  EXPENSES: IconReceipt,
  REPORT: IconFileAnalytics,
  PAYROLL: IconWallet,
  COMMUNICATION: IconMessageReport,
  INVENTORY: IconBox,
};

const getIcon = (label: string) => {
  const upperLabel = label.toUpperCase();
  const IconComponent = iconMap[upperLabel];
  if (IconComponent) {
    return <IconComponent size={16} stroke={1} />;
  }
  return null;
};

export function NavbarList({ mainLinks }: { mainLinks: any[] }) {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef<HTMLDivElement>(null);

  const sortedLinks = [...mainLinks].sort((a, b) => a.order - b.order);
  const isActive = (path: string) => location.pathname === path;

  const isParentActive = (link: any) => {
    if (link.link && isActive(link.link)) return true;
    if (link.dropdown) {
      return link.dropdown.some(
        (child: any) => child.link && isActive(child.link)
      );
    }
    return false;
  };

  const itemsToMove = ["REPORT", "PAYROLL"];
  const filteredMainLinks = sortedLinks.filter(l => !itemsToMove.includes(l.label.toUpperCase()));
  const moreSubLinks = sortedLinks.filter(l => itemsToMove.includes(l.label.toUpperCase()));

  const finalLinks = [...filteredMainLinks];
  if (moreSubLinks.length > 0) {
    finalLinks.push({
      label: "MORE",
      order: 100,
      dropdown: moreSubLinks
    });
  }

  const renderLink = (link: any, isInsideMore = false) => {
    if (link.dropdown) {
      const sortedDropdown = [...link.dropdown].sort(
        (a, b) => a.order - b.order
      );

      if (isInsideMore) {
        // Flat list with headers for nested items in "MORE"
        return (
          <React.Fragment key={link.label}>
            <Menu.Divider />
            <Menu.Label style={{ textTransform: "uppercase", fontWeight: 700, fontSize: "11px", color: "#868e96" }}>
              {link.label}
            </Menu.Label>
            {sortedDropdown.map((subItem: any) => (
              <Menu.Item
                key={subItem.label}
                component={Link}
                to={subItem.link}
                style={{
                  fontWeight: 600,
                  fontSize: "13px",
                  textTransform: "uppercase",
                  color: isActive(subItem.link) ? "#f2354b" : "black",
                  padding: "8px 20px",
                }}
              >
                {subItem.label}
              </Menu.Item>
            ))}
          </React.Fragment>
        );
      }

      const menuItems = sortedDropdown.map((item: any) => {
        if (item.dropdown) {
          return renderLink(item, true);
        }
        return (
          <Menu.Item
            key={item.label}
            style={{
              fontWeight: 600,
              fontSize: "13px",
              textTransform: "uppercase",
              color: isActive(item.link) ? "#f2354b" : "black",
              height: "36px",
              textDecoration: "none",
              padding: 0,
              letterSpacing: "0.3px"
            }}
          >
            {item.link ? (
              <Link
                to={item.link}
                style={{
                  color: isActive(item.link) ? "#f2354b" : "black",
                  textDecoration: "none",
                  fontWeight: 600,
                  fontSize: "13px",
                  textTransform: "uppercase",
                  height: "36px",
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  padding: "8px 10px",
                  gap: "6px"
                }}
              >
                {item.label}
              </Link>
            ) : (
              <div style={{ padding: "0 8px", display: "flex", alignItems: "center", gap: "8px" }}>
                {item.label}
              </div>
            )}
          </Menu.Item>
        );
      });

      const parentActive = isParentActive(link);

      return (
        <Menu
          key={link.label}
          opened={openDropdown === link.label}
          onOpen={() => setOpenDropdown(link.label)}
          onClose={() => setOpenDropdown(null)}
          withinPortal
          transitionProps={{ exitDuration: 0 }}
        >
          <Menu.Target>
            <button
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "10px 14px",
                display: "flex",
                alignItems: "center",
                gap: "5px",
                color: parentActive ? "#f2354b" : "black",
                fontWeight: 600,
                fontSize: "13.5px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                transition: "all 0.2s ease",
                whiteSpace: "nowrap"
              }}
              onClick={(event) => {
                event.preventDefault();
                if (link.link) {
                  navigate(link.link);
                } else {
                  setOpenDropdown(
                    openDropdown === link.label ? null : link.label
                  );
                }
              }}
            >
              {getIcon(link.label)}
              <span>{link.label}</span>
              <IconChevronDown size={14} stroke={1.5} />
            </button>
          </Menu.Target>
          <Menu.Dropdown>{menuItems}</Menu.Dropdown>
        </Menu>
      );
    }

    // No dropdown, render regular link
    return (
      <Text
        component={Link}
        key={link.label}
        to={link.link || "#"}
        style={{
          fontWeight: 600,
          fontSize: "13.5px",
          textTransform: "uppercase",
          color: isActive(link.link) ? "#f2354b" : "black",
          padding: "10px 8px",
          textDecoration: "none",
          display: "flex",
          alignItems: "center",
          gap: "6px",
          letterSpacing: "0.5px",
          transition: "all 0.2s ease",
          whiteSpace: "nowrap"
        }}
      >
        {getIcon(link.label)}
        {link.label}
      </Text>
    );
  };

  // const renderLink = (link: any) => {
  //   if (link.dropdown) {
  //     const sortedDropdown = [...link.dropdown].sort((a, b) => a.order - b.order);

  //     const menuItems = sortedDropdown.map((item: any) => (
  //       <Menu.Item key={item.label}>
  //         {item.link ? (
  //           <Link
  //             to={item.link}
  //             style={{ textDecoration: "none", display: "block", width: "100%" }}
  //           >
  //             <Text
  //               style={{
  //                 fontWeight: 700,
  //                 fontSize: "12px",
  //                 textTransform: "uppercase",
  //                 color: isActive(item.link) ? "#f2354b" : "black",
  //                 height: "26px"
  //               }}
  //             >
  //               {item.label}
  //             </Text>
  //           </Link>
  //         ) : (
  //           <Text
  //             style={{ fontWeight: 700, fontSize: "12px", textTransform: "uppercase", height: "26px" }}
  //           >
  //             {item.label}
  //           </Text>
  //         )}
  //       </Menu.Item>
  //     ));

  //     const parentActive = isParentActive(link);

  //     return (
  //       <Menu
  //         key={link.label}
  //         opened={openDropdown === link.label}
  //         onOpen={() => setOpenDropdown(link.label)}
  //         onClose={() => setOpenDropdown(null)}
  //         withinPortal
  //         transitionProps={{ exitDuration: 0 }}
  //       >
  //         <Menu.Target>
  //           <button
  //             style={{
  //               background: "none",
  //               border: "none",
  //               cursor: "pointer",
  //               padding: "8px 18px",
  //               display: "flex",
  //               alignItems: "center",
  //               gap: "5px",
  //               color: parentActive ? "#f2354b" : "black",
  //               fontWeight: 700,
  //               fontSize: "12px",
  //               textTransform: "uppercase",
  //             }}
  //             onClick={(event) => {
  //               event.preventDefault();

  //               if (link.link) {
  //                 navigate(link.link);
  //               } else {
  //                 setOpenDropdown(openDropdown === link.label ? null : link.label);
  //               }
  //             }}
  //           >
  //             <span>{link.label}</span>
  //             <IconChevronDown size={14} stroke={1.5} />
  //           </button>
  //         </Menu.Target>
  //         <Menu.Dropdown>{menuItems}</Menu.Dropdown>
  //       </Menu>
  //     );
  //   }

  //   return (
  //     <Text
  //       component={Link}
  //       key={link.label}
  //       to={link.link || "#"}
  //       style={{
  //         fontWeight: 700,
  //         fontSize: "12px",
  //         textTransform: "uppercase",
  //         color: isActive(link.link) ? "#f2354b" : "black",
  //         padding: "8px 12px",
  //         textDecoration: "none",
  //       }}
  //     >
  //       {link.label}
  //     </Text>
  //   );
  // };

  return (
    <div
      ref={navRef}
      style={{
        display: "flex",
        alignItems: "center",
        overflowX: "auto",
        overflowY: "hidden",
        width: "100%",
        scrollbarWidth: "none", // For Firefox
        msOverflowStyle: "none", // For IE/Edge
      }}
      className="hide-scrollbar"
    >
      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <Group spacing="0" noWrap style={{ flexWrap: "nowrap" }}>
        {finalLinks.map((l) => renderLink(l))}
      </Group>
    </div>
  );
}
